import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, X, Search, MousePointerClick, UserPlus, Loader2, Copy } from 'lucide-react';
import { ROLE_LABELS, formatDate } from '../lib/utils';
import RecordAuditPanel from '../components/RecordAuditPanel';
import OutcomeMetrics from '../components/OutcomeMetrics';

const roleColors = {
  client: 'bg-teal-50 text-teal-700 border-teal-200',
  peer_specialist: 'bg-sky-50 text-sky-700 border-sky-200',
  facilitator: 'bg-orange-50 text-orange-700 border-orange-200',
  coordinator: 'bg-amber-50 text-amber-700 border-amber-200',
  court_officer: 'bg-slate-50 text-slate-700 border-slate-200',
  admin: 'bg-violet-50 text-violet-700 border-violet-200',
  referral_source: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  resource_partner: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  housing_staff: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  housing_coordinator: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  housing_resident: 'bg-teal-50 text-teal-700 border-teal-200',
};

const STAFF_ROLES = ['peer_specialist', 'facilitator', 'coordinator', 'admin', 'network_admin', 'resource_partner', 'housing_staff', 'housing_coordinator', 'referral_source', 'court_officer'];

export default function UsersPage() {
  const { user: currentUser, axiosAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  const groupParam = searchParams.get('group');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [auditUser, setAuditUser] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ full_name: '', email: '', phone: '', role: 'facilitator', password: '' });
  const api = axiosAuth();

  const isAdmin = ['admin', 'network_admin'].includes(currentUser?.role);

  const genPassword = () => {
    const words = ['Reach', 'Build', 'Rise', 'Grow', 'Hope', 'Trust', 'Lead'];
    const word = words[Math.floor(Math.random() * words.length)];
    const num = Math.floor(1000 + Math.random() * 9000);
    return `${word}${num}Welcome`;
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.full_name) return toast.error('Email and name required');
    const pw = newUser.password || genPassword();
    setCreating(true);
    try {
      await api.post('/admin/create-user', { ...newUser, password: pw });
      toast.success(`Account created. Temp password: ${pw}`, { duration: 15000 });
      try { await navigator.clipboard.writeText(`Email: ${newUser.email}\nTemp password: ${pw}`); } catch { /* noop */ }
      setAddOpen(false);
      setNewUser({ full_name: '', email: '', phone: '', role: 'facilitator', password: '' });
      // Reload
      const res = await api.get('/users');
      setUsers(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    }
    setCreating(false);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/users');
        setUsers(res.data || []);
      } catch {
        setUsers([]);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tally by role
  const tallies = useMemo(() => {
    const t = {};
    users.forEach(u => { t[u.role] = (t[u.role] || 0) + 1; });
    return t;
  }, [users]);

  // Filter
  const filteredUsers = useMemo(() => {
    let result = users;
    if (roleParam) result = result.filter(u => u.role === roleParam);
    if (groupParam === 'staff') result = result.filter(u => STAFF_ROLES.includes(u.role));
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(u => (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
    }
    return result;
  }, [users, roleParam, groupParam, search]);

  const setRole = (role) => {
    if (role === roleParam) {
      // Toggle off
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('role');
      setSearchParams(newParams);
    } else {
      setSearchParams({ role });
    }
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearch('');
  };

  const activeFilters = roleParam || groupParam || search;

  // Sorted role list for cards (most populated first)
  const roleCards = Object.entries(tallies)
    .filter(([role]) => role)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>User Management</h1>
          <p className="text-muted-foreground text-sm mt-1" data-testid="users-count">
            {filteredUsers.length} {activeFilters ? `of ${users.length} (filtered)` : 'total users'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setAddOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1.5" data-testid="add-staff-btn">
            <UserPlus size={14} /> Add Staff
          </Button>
        )}
      </div>

      {/* Role tally cards — click to filter */}
      <div>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2"><MousePointerClick size={11} /> Tap a role to filter</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {roleCards.map(([role, count]) => {
            const isActive = role === roleParam;
            return (
              <Card
                key={role}
                onClick={() => setRole(role)}
                className={`border-2 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all ${isActive ? 'ring-2 ring-teal-500 border-teal-300' : 'border-transparent'} ${roleColors[role] || 'bg-slate-50'}`}
                data-testid={`role-tally-${role}`}
              >
                <CardContent className="p-4">
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs font-medium">{ROLE_LABELS[role] || role}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Search + active filters bar */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-9 bg-white"
            data-testid="users-search-input"
          />
        </div>
        {activeFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 px-3 py-2 bg-slate-100 rounded-full"
            data-testid="users-clear-filters"
          >
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      {/* User list */}
      <div className="space-y-3">
        {filteredUsers.map(u => (
          <Card key={u.id} onClick={() => setAuditUser(u)} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-all" data-testid={`user-card-${u.id}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center text-base font-semibold text-teal-700 shrink-0">{u.full_name?.charAt(0)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{u.full_name}</p>
                <p className="text-sm text-muted-foreground">{u.email}</p>
                {u.created_at && <p className="text-xs text-muted-foreground">Joined {formatDate(u.created_at)}</p>}
              </div>
              <Badge className={roleColors[u.role] || 'bg-muted'} variant="secondary">{ROLE_LABELS[u.role] || u.role}</Badge>
              <Badge className="bg-emerald-50 text-emerald-700" variant="secondary">{u.status || 'active'}</Badge>
            </CardContent>
          </Card>
        ))}
        {filteredUsers.length === 0 && (
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-8 text-center">
              <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No users found.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* User Record Audit Dialog */}
      <Dialog open={!!auditUser} onOpenChange={(o) => !o && setAuditUser(null)}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{auditUser?.full_name}</DialogTitle>
            <DialogDescription>{auditUser?.email} · {ROLE_LABELS[auditUser?.role] || auditUser?.role}</DialogDescription>
          </DialogHeader>
          {auditUser && (
            <>
              {(auditUser.role === 'client' || auditUser.role === 'housing_resident') && (
                <OutcomeMetrics participantId={auditUser.id} />
              )}
              <RecordAuditPanel participantId={auditUser.id} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="add-staff-dialog">
          <DialogHeader>
            <DialogTitle>Add Staff Account</DialogTitle>
            <DialogDescription>Create an account for a team member. They cannot self-register.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Full Name</Label>
              <Input value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} placeholder="Jane Doe" data-testid="new-staff-name" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="jane@purposedrivenpeersupport.com" data-testid="new-staff-email" />
            </div>
            <div>
              <Label className="text-xs">Phone (optional)</Label>
              <Input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} placeholder="+15555551234" data-testid="new-staff-phone" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="peer_specialist">Peer Support Specialist</SelectItem>
                  <SelectItem value="facilitator">Facilitator</SelectItem>
                  <SelectItem value="coordinator">Program Coordinator</SelectItem>
                  <SelectItem value="admin">Executive Director / Admin</SelectItem>
                  <SelectItem value="housing_staff">Housing Staff</SelectItem>
                  <SelectItem value="housing_coordinator">Housing Coordinator</SelectItem>
                  <SelectItem value="referral_source">Referral Source</SelectItem>
                  <SelectItem value="resource_partner">Resource Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                Temporary Password
                <button type="button" onClick={() => setNewUser({ ...newUser, password: genPassword() })} className="text-[10px] text-teal-600 underline">auto-generate</button>
              </Label>
              <Input value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Leave blank to auto-generate" data-testid="new-staff-password" />
              <p className="text-[10px] text-muted-foreground mt-1">Share this with them in person or via secure channel. They&apos;ll be prompted to change it on first login.</p>
            </div>
            <Button onClick={createUser} disabled={creating} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1.5" data-testid="create-staff-btn">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              Create Account
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
